import { activeTransactionSchedulesFromGoalAccount } from '@common/financialEntities';
import { calculateForecastDate } from '@common/forecasting';
import { Goal } from '@common/goals';
import { CurrentRateInfo } from '@common/graphql/generated';
import {
    getGoalAccountBalance,
    getMyGoalAccount,
    getMyGoalOnboardingData,
    getMyGoalTransactionSchedules,
    getPartnerGoalAccount,
    getPartnerGoalAccountOnboarding,
    getPartnerGoalOnboardingData,
    getPartnerGoalTransactionSchedules,
    transactionSchedulePlansFromTransactionSchedules,
} from '@common/react/goals';
import { TransactionSchedulePlan } from '@common/types';
import { dt } from '@common/util/dateTime';
import { configByGoalType } from '../config';
import {
    calculateComplimentaryTransactionSchedulePlan,
    deriveTransactionSchedulePlan,
    getGoalForecastStartAmount,
} from './goalsHelpers';

export type PartnerGoalAccountData = {
    startAmount: number;
    transactionSchedulePlans?: TransactionSchedulePlan[];
    annualReturn: number;
};

export const getPartnerGoalAccountData = (
    goal: Goal,
    currentRateInfo: CurrentRateInfo
): PartnerGoalAccountData | undefined => {
    const partnerGoalAccountOnboarding = getPartnerGoalAccountOnboarding(goal);
    const goalConfig = configByGoalType[goal.type];

    if (
        !partnerGoalAccountOnboarding ||
        !partnerGoalAccountOnboarding.confirmedAt ||
        !partnerGoalAccountOnboarding.info.riskScore
    ) {
        return;
    }

    const partnerGoalAccount = getPartnerGoalAccount(goal);
    const balance = getGoalAccountBalance(partnerGoalAccount);
    const partnerTransactionSchedules =
        activeTransactionSchedulesFromGoalAccount(partnerGoalAccount);
    const riskScore =
        partnerGoalAccount?.info.riskScore ??
        partnerGoalAccountOnboarding.info.riskScore;
    const annualReturn = goalConfig.getAnnualReturn({
        investmentType: riskScore === 0 ? 'HIGH_YIELD_CASH' : undefined,
        riskScore,
        currentRateInfo,
    });

    let partnerTransactionSchedulePlans: TransactionSchedulePlan[] = [];
    if (partnerTransactionSchedules.length > 0) {
        partnerTransactionSchedulePlans =
            transactionSchedulePlansFromTransactionSchedules(
                partnerTransactionSchedules
            );
    } else if (partnerGoalAccountOnboarding.info.transactionSchedulePlan) {
        partnerTransactionSchedulePlans = [
            partnerGoalAccountOnboarding.info.transactionSchedulePlan,
        ];
    }

    return {
        startAmount: getGoalForecastStartAmount(
            balance,
            partnerGoalAccountOnboarding.info.initialDepositPlan
        ),
        transactionSchedulePlans: partnerTransactionSchedulePlans,
        annualReturn,
    };
};

export const getGoalContextInfo = (
    goal: Goal,
    currentRateInfo: CurrentRateInfo
) => {
    const isSharedGoal = goal.shareStatus === 'SHARED';
    const config = configByGoalType[goal.type];

    const myGoalOnboardingData = getMyGoalOnboardingData(goal);
    const myGoalAccount = getMyGoalAccount(goal);
    const myGoalAccountBalance = getGoalAccountBalance(myGoalAccount);
    const myTransactionSchedules = getMyGoalTransactionSchedules(goal);
    const myRiskScore =
        myGoalAccount?.info.riskScore ?? myGoalOnboardingData?.riskScore;
    const myAnnualReturn = config.getAnnualReturn({
        investmentType: myRiskScore === 0 ? 'HIGH_YIELD_CASH' : undefined,
        riskScore: myRiskScore,
        currentRateInfo,
    });
    const myForecastStartAmount = getGoalForecastStartAmount(
        myGoalAccountBalance,
        myGoalOnboardingData.initialDepositPlan
    );
    const myTransactionSchedulePlans = myGoalAccount
        ? // If user is already investing, use existing transaction schedules.
          transactionSchedulePlansFromTransactionSchedules(
              myTransactionSchedules ?? []
          )
        : // Otherwise, derive a plan from onboarding info.
          [
              deriveTransactionSchedulePlan(
                  {
                      annualReturn: myAnnualReturn,
                      initialDepositPlan:
                          myGoalOnboardingData.initialDepositPlan ?? 0,
                      transactionSchedulePlan:
                          myGoalOnboardingData.transactionSchedulePlan,
                      goal,
                      currentRateInfo,
                  },
                  { resetStartDate: true, clampToTargetAmount: true }
              ),
          ];
    const myFirstTransactionSchedulePlan = myTransactionSchedulePlans[0];

    const partnerGoalOnboardingData = getPartnerGoalOnboardingData(goal);
    const partnerGoalAccount = getPartnerGoalAccount(goal);
    const partnerGoalAccountBalance = getGoalAccountBalance(partnerGoalAccount);
    const partnerTransactionSchedules =
        getPartnerGoalTransactionSchedules(goal);
    const partnerRiskScore =
        partnerGoalAccount?.info.riskScore ??
        partnerGoalOnboardingData?.riskScore;
    const partnerAnnualReturn = config.getAnnualReturn({
        investmentType: partnerRiskScore === 0 ? 'HIGH_YIELD_CASH' : undefined,
        riskScore: partnerRiskScore,
        currentRateInfo,
    });
    const partnerForecastStartAmount = getGoalForecastStartAmount(
        partnerGoalAccountBalance,
        partnerGoalOnboardingData.initialDepositPlan
    );

    // TODO: Try to refactor GoalOnboardingPlanView to reuse this
    let partnerTransactionSchedulePlans: TransactionSchedulePlan[] | undefined;
    let forecastCompletionDate: string | undefined;
    if (isSharedGoal) {
        partnerTransactionSchedulePlans = partnerGoalAccount
            ? // If partner is already investing, use their current values
              transactionSchedulePlansFromTransactionSchedules(
                  partnerTransactionSchedules ?? []
              )
            : // If target amount exists, calculate a plan to reach target
              goal.targetAmount && goal.targetDate
              ? // If partner's not yet investing, calculate a plan for them using
                //  *their* return data that's complimentary to my plan
                [
                    calculateComplimentaryTransactionSchedulePlan({
                        existingPlan: {
                            startAmount: myForecastStartAmount,
                            transactionSchedulePlan:
                                myFirstTransactionSchedulePlan,
                            annualReturn: myAnnualReturn,
                        },
                        annualReturn: partnerAnnualReturn,
                        startAmount: partnerForecastStartAmount,
                        startDate: dt().toDateStr(),
                        targetAmount: goal.targetAmount,
                        targetDate: goal.targetDate,
                    }),
                ]
              : // Otherwise duplicate my first plan as the partner's plan
                myFirstTransactionSchedulePlan
                ? [myFirstTransactionSchedulePlan]
                : undefined;
        forecastCompletionDate = goal.targetAmount
            ? calculateForecastDate({
                  startDate: dt().toDateStr(),
                  targetAmount: goal.targetAmount,
                  targetDate: goal.targetDate,
                  plans: [
                      {
                          startAmount: myForecastStartAmount,
                          annualGrowthRate: myAnnualReturn,
                          transactionSchedulePlans: myTransactionSchedulePlans,
                      },
                      {
                          startAmount: partnerForecastStartAmount,
                          annualGrowthRate: partnerAnnualReturn,
                          transactionSchedulePlans:
                              partnerTransactionSchedulePlans,
                      },
                  ],
              })
            : undefined;
    } else if (goal.isMine) {
        // My Private/Personal goal
        forecastCompletionDate = goal.targetAmount
            ? calculateForecastDate({
                  startDate: dt().toDateStr(),
                  targetAmount: goal.targetAmount,
                  targetDate: goal.targetDate,
                  plans: [
                      {
                          startAmount: myForecastStartAmount,
                          annualGrowthRate: myAnnualReturn,
                          transactionSchedulePlans: myTransactionSchedulePlans,
                      },
                  ],
              })
            : undefined;
    } else {
        // Partner's Personal goal
        partnerTransactionSchedulePlans = partnerGoalAccount
            ? transactionSchedulePlansFromTransactionSchedules(
                  partnerTransactionSchedules ?? []
              )
            : partnerGoalOnboardingData.transactionSchedulePlan
              ? [partnerGoalOnboardingData.transactionSchedulePlan]
              : [];
        forecastCompletionDate =
            goal.targetAmount &&
            (partnerTransactionSchedulePlans.length ||
                partnerForecastStartAmount > 0)
                ? calculateForecastDate({
                      startDate: dt().toDateStr(),
                      targetAmount: goal.targetAmount,
                      targetDate: goal.targetDate,
                      plans: [
                          {
                              startAmount: partnerForecastStartAmount,
                              annualGrowthRate: partnerAnnualReturn,
                              transactionSchedulePlans:
                                  partnerTransactionSchedulePlans,
                          },
                      ],
                  })
                : undefined;
    }

    return {
        myGoalInfo: {
            onboardingData: myGoalOnboardingData,
            account: myGoalAccount,
            accountBalance: myGoalAccountBalance,
            transactionSchedules: myTransactionSchedules,
            transactionSchedulePlans: myTransactionSchedulePlans,
            annualReturn: myAnnualReturn,
            forecastStartAmount: myForecastStartAmount,
        },
        partnerGoalInfo: {
            onboardingData: partnerGoalOnboardingData,
            account: partnerGoalAccount,
            accountBalance: partnerGoalAccountBalance,
            transactionSchedules: partnerTransactionSchedules,
            transactionSchedulePlans: partnerTransactionSchedulePlans,
            annualReturn: partnerAnnualReturn,
            forecastStartAmount: partnerForecastStartAmount,
        },
        forecastCompletionDate,
    };
};
